import { Component, ReactNode } from 'react';
import styles from './fullscreen-host.module.scss';

interface Props {
    children: ReactNode;
    className?: string;
}

interface State {
    expanded: boolean;
}

// Wraps an editor surface and renders an expand/collapse button in the
// top-right. When expanded, the host becomes position:fixed full-viewport
// with a body class that locks page scroll; Esc collapses back.
//
// Not persisted across page loads on purpose — users who don't remember
// turning fullscreen on shouldn't be surprised by a page that looks
// "different" on revisit. Every visit starts in the normal layout.
export class FullscreenHost extends Component<Props, State> {
    state: State = { expanded: false };

    private escHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && this.state.expanded) this.toggle();
    };

    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('keydown', this.escHandler);
            document.body.classList.remove('xc-fullscreen');
        }
    }

    toggle = () => {
        const expanded = !this.state.expanded;
        this.setState({ expanded });
        if (typeof window === 'undefined') return;
        document.body.classList.toggle('xc-fullscreen', expanded);
        if (expanded) window.addEventListener('keydown', this.escHandler);
        else window.removeEventListener('keydown', this.escHandler);
    };

    render() {
        const { children, className } = this.props;
        const { expanded } = this.state;
        const cls = [styles.host, className, expanded ? styles.expanded : '']
            .filter(Boolean).join(' ');
        return (
            <div className={cls}>
                <button type="button" className={styles.toggle} onClick={this.toggle}
                    aria-label={expanded ? 'Collapse to page view' : 'Expand editor to fullscreen'}
                    title={expanded ? 'Collapse (Esc)' : 'Expand editor (Esc to close)'}>
                    <span className={styles.toggleIcon} aria-hidden="true">{expanded ? '⤡' : '⛶'}</span>
                    <span className={styles.toggleLabel}>{expanded ? 'Collapse' : 'Expand'}</span>
                </button>
                {children}
            </div>
        );
    }
}

export default FullscreenHost;
